import React from 'react';
import config from '../../../../config';
import { TdsBlock } from '@scania/tegel-react';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { docco } from 'react-syntax-highlighter/src/styles/hljs';
import DivTdsBody01 from '../../../../components/atoms/Typography/DivTdsBody01';
import ApiDescriptionContainer from '../ApiDescriptionContainer';
import LiContainer from '../../../../components/atoms/List/LiContainer';
import LiWithTitle from '../../../../components/atoms/List/LiWithTitle';
import MainHeader from '../../../../components/atoms/Typography/MainHeader';
import SectionLayout from '../../../../components/atoms/Section/SectionLayout';

const exampleRequest = {
  data: {
    type: 'users',
    attributes: {
      CustomerId: 'YourReferenceId',
      Email: 'user.one@yourcompany.eu',
      ViewAccounts: ['ACCOUNT_ONE', 'ACCOUNT_TWO'],
      EditAccountAlias: ['ACCOUNT_ONE']
    }
  }
};

const exampleResponse = {
  data: {
    id: 'user.one@yourcompany.eu',
    type: 'users',
    attributes: {
      Email: 'user.one@yourcompany.eu',
      CustomerId: 'YourReferenceId',
      ViewAccounts: ['ACCOUNT_ONE', 'ACCOUNT_TWO'],
      EditAccountAlias: ['ACCOUNT_ONE'],
      Groups: [],
      IsAdmin: false,
      IsGroup: false
    }
  }
};

function DevelopersAccessApiPatchUserPermissions() {
  return (
    <>
      <div className={'tds-col-xs-12 tds-col-lg-6'}>
        <ApiDescriptionContainer
          method={'patch'}
          path={'/v1/auth/customers/{customer_id}/users/{user_id}'}
          description={
            'This endpoint alters an existing user that is associated with the specified customer.'
          }>
          <SectionLayout header={'Path Parameters'}>
            <LiContainer>
              <LiWithTitle title={'customer_id'}>
                The id of the customer where to lookup the specific user.
              </LiWithTitle>
              <LiWithTitle title={'user_id'}>
                The id of the user to patch, usually this is the users e-mail address.
              </LiWithTitle>
            </LiContainer>
          </SectionLayout>
          <SectionLayout header={'Request Body (as JSON object)'}>
            <ul>
              <LiWithTitle title={'data.type [REQUIRED]'}>
                Internal use, only allowed value is{' '}
                <b>
                  <code>users</code>
                </b>
                .
              </LiWithTitle>
              <LiWithTitle title={'data.attributes.CustomerId [REQUIRED]'}>
                The customer id that the user is associated with, should be the same as specified in
                the Path Parameters.
              </LiWithTitle>
              <LiWithTitle title={'data.attributes.Email [REQUIRED]'}>
                The user id (email) that will be altered, should be the same as specified in the
                Path Parameters.
              </LiWithTitle>
              <LiWithTitle title={'data.attributes.ViewAccounts'}>
                Specify (as a list of strings) all accounts/subscriptions that the user should be
                allowed to view.
                <br />
                Default value is{' '}
                <b>
                  <code>[]</code>
                </b>
                .
              </LiWithTitle>
              <LiWithTitle title={'data.attributes.EditAccountAlias'}>
                Specify (as a list of strings) all accounts/subscriptions that the user should be
                <br />
                Default value is{' '}
                <b>
                  <code>[]</code>
                </b>
                .
              </LiWithTitle>
              <LiWithTitle title={'data.attributes.IsAdmin'}>
                Specifies if the user should be assigned as administrator or not, allowed values:{' '}
                <code>true | false</code>
                .
                <br />
                Default value is{' '}
                <b>
                  <code>false</code>
                </b>
                .
              </LiWithTitle>
            </ul>
          </SectionLayout>
          <SectionLayout header={'Required Permissions'}>
            <DivTdsBody01>
              Admin permissions for the specific customer is required in order to get a successful
              response.
            </DivTdsBody01>
          </SectionLayout>
        </ApiDescriptionContainer>
      </div>
      <div className={'tds-col-xs-12 tds-col-lg-6'}>
        <TdsBlock>
          <MainHeader>Example</MainHeader>
          <h3>Request</h3>
          <SyntaxHighlighter language="sh" style={docco} wrapLongLines>
            {"curl '" +
              config.API_GATEWAY_AUTH_URL +
              '/v1/auth/customers/YourReferenceId/users\' -X PATCH -H "Authorization: Basic $API_KEY" ' +
              `-d '${JSON.stringify(exampleRequest)}'`}
          </SyntaxHighlighter>
          <h3>Response</h3>
          <h4>Status Codes</h4>
          <code>
            200 <span className="code-general-divider">|</span> 400{' '}
            <span className="code-general-divider">|</span> 401{' '}
            <span className="code-general-divider">|</span> 409
          </code>
          <h4>
            Payload Data <span style={{ fontSize: '0.75rem' }}>(200)</span>
          </h4>
          <SyntaxHighlighter language="json" style={docco} wrapLongLines>
            {JSON.stringify(exampleResponse, null, 2)}
          </SyntaxHighlighter>
        </TdsBlock>
      </div>
    </>
  );
}

export default DevelopersAccessApiPatchUserPermissions;
